import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { formatCurrency } from '../../lib/format.js'
import ArticleFormModal from './ArticleFormModal.jsx'

/*
 * Article catalog — inventory §7. Server-side: GET /articles/ (paginated) for
 * the full list, GET /articles/search?q= when searching (uses the real search
 * endpoint, unlike the legacy client-only filter). Create/edit/duplicate via the
 * internal modal; delete via the internal confirm modal.
 */
export default function ArticlesListPage() {
  const { t } = useTranslation()
  const { confirm, toast } = useFeedback()
  const { currentCompanyId } = useCompany()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [vatRates, setVatRates] = useState([])
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // {entityId?, initial?}
  const [sort, setSort] = useState(null) // { key, dir } — server-side (list path)

  const toggleSort = (key) => {
    setPage(1)
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null))
  }

  useEffect(() => {
    if (!currentCompanyId) return
    api.get('/vat-rates/', { params: { active_only: true } }).then(({ data }) => setVatRates(data?.vat_rates || [])).catch(() => {})
  }, [currentCompanyId])

  const fetchRows = useCallback(() => {
    if (!currentCompanyId) return undefined
    setLoading(true)
    const q = search.trim()
    const listParams = { page, per_page: perPage }
    if (sort) listParams.sort = `${sort.key}_${sort.dir}`
    const req = q
      ? api.get('/articles/search', { params: { q } }).then(({ data }) => ({ articles: data?.articles || [], pagination: null }))
      : api.get('/articles/', { params: listParams }).then(({ data }) => ({ articles: data?.articles || [], pagination: data?.pagination }))
    return req.then(({ articles, pagination: pg }) => {
      setRows(articles)
      setPagination(pg || { page: 1, pages: 1, total: articles.length })
    }).finally(() => setLoading(false))
  }, [currentCompanyId, page, perPage, search, sort])

  useEffect(() => { fetchRows() }, [fetchRows])

  const onSearch = (v) => { setSearch(v); setPage(1) }

  const remove = async (row) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('articles.confirmDelete.title'), message: t('articles.confirmDelete.message', { code: row.code }), confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/articles/${row.id}`); toast(t('articles.toast.deleted'), 'success'); fetchRows() }
    catch { toast(t('articles.toast.error'), 'error') }
  }

  const duplicate = (row) => setModal({ initial: { ...row, id: undefined, code: `${row.code}-copie` } })

  const action = <Button variant="primary" icon="plus" onClick={() => setModal({})}>{t('articles.new')}</Button>
  const th = (label, align = 'left', sortKey = null) => {
    const active = sortKey && sort?.key === sortKey
    const base = { textAlign: align, padding: '12px 18px', fontSize: 11.5, fontWeight: 600, color: active ? 'var(--color-primary)' : 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }
    if (!sortKey || search.trim()) return <th style={base}>{label}</th>
    return (
      <th style={{ ...base, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(sortKey)} title={t('common.sort')}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          {label}<span style={{ opacity: active ? 1 : 0.35, fontSize: 10 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
        </span>
      </th>
    )
  }

  return (
    <Page title={t('articles.title')} action={action}>
      <Card style={{ marginBottom: 18 }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={17} /></span>
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={t('articles.searchPlaceholder')} style={{ height: 42, paddingLeft: 38 }} aria-label={t('common.search')} />
        </div>
      </Card>

      <Card padding={0}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '52px 28px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-muted)', display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Icon name="article" size={38} strokeWidth={1.5} /></div>
            <div style={{ fontFamily: 'var(--font-title)', fontWeight: 700, color: 'var(--color-ink)', marginBottom: 14 }}>{t(search ? 'articles.emptyFiltered' : 'articles.empty')}</div>
            {!search && <div style={{ display: 'flex', justifyContent: 'center' }}><Button variant="primary" icon="plus" onClick={() => setModal({})}>{t('articles.new')}</Button></div>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
                {th(t('articles.cols.code'), 'left', 'code')}{th(t('articles.cols.description'), 'left', 'description')}{th(t('articles.cols.vat'))}{th(t('articles.cols.unitPrice'), 'right', 'unit_price')}{th(t('common.actions'), 'right')}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                    <td style={{ padding: '12px 18px', fontWeight: 600, color: 'var(--color-ink)', whiteSpace: 'nowrap' }}>{r.code}</td>
                    <td style={{ padding: '12px 18px', color: 'var(--color-text)', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                    <td style={{ padding: '12px 18px' }}><Badge tone="neutral">{r.vat_rate?.rate ?? 0}%</Badge></td>
                    <td style={{ padding: '12px 18px', textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.unit_price == null ? '—' : formatCurrency(r.unit_price)}</td>
                    <td style={{ padding: '12px 18px' }}>
                      <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
                        <IconButton icon="copy" title={t('articles.actions.duplicate')} onClick={() => duplicate(r)} />
                        <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setModal({ entityId: r.id, initial: r })} />
                        <IconButton icon="trash" title={t('common.delete')} variant="danger" onClick={() => remove(r)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && rows.length > 0 && !search && (
          <div style={{ padding: '0 18px 16px' }}>
            <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t('articles.unit')} perPage={perPage}
              onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }} onPerPage={(n) => { setPerPage(n); setPage(1) }} />
          </div>
        )}
      </Card>

      <ArticleFormModal open={!!modal} vatRates={vatRates} entityId={modal?.entityId} initial={modal?.initial}
        onClose={() => setModal(null)} onSaved={() => { toast(t('articles.toast.saved'), 'success'); fetchRows() }} />
    </Page>
  )
}
